import type { MeasurementAnswerInput } from '@/src/dal/measurement-dal'
import type { MeasurementDefinitionDetail } from '@/src/dal/session-dal'
import type { User } from '@/src/dal/user-dal'

import {
  getCompletedMeasurementsCount,
  measurementRequiresRecording,
} from '../components/VoiceAssessmentFlow.shared'

export type VoiceAssessmentVoiceType = User['voiceType'] | null

export function getVoiceAssessmentVoiceType(
  user?: Pick<User, 'voiceType'> | null,
): VoiceAssessmentVoiceType {
  return user?.voiceType ?? null
}

export function getVoiceAssessmentReflectionAnalytics(value: string): {
  hasReflection: boolean
  reflectionLength: number
} {
  const trimmed = value.trim()
  return {
    hasReflection: trimmed.length > 0,
    reflectionLength: trimmed.length,
  }
}

function countAnsweredVoiceAssessmentQuestions(
  answers: readonly MeasurementAnswerInput[],
): number {
  return answers.filter((answer) => answer.choiceIndices.length > 0).length
}

export function getVoiceAssessmentCompletedMeasurements(params: {
  measurementIndex: number
  measurementDefinitions: readonly MeasurementDefinitionDetail[]
}): number {
  return getCompletedMeasurementsCount(
    { measurementIndex: params.measurementIndex },
    params.measurementDefinitions,
  )
}

export function getVoiceAssessmentMeasurementAnalytics(params: {
  measurementDefinitions: readonly MeasurementDefinitionDetail[]
  measurementQuestionAnswers: readonly MeasurementAnswerInput[][]
  persistedMeasurementIds: readonly (number | null)[]
  confirmedRecordingUris: readonly (string | null)[]
  confirmedRecordingBlobs: readonly (Blob | null)[]
  measurementIndex: number
}) {
  const measurementDefinition =
    params.measurementDefinitions[params.measurementIndex]
  const answers =
    params.measurementQuestionAnswers[params.measurementIndex] ?? []

  return {
    measurementId:
      params.persistedMeasurementIds[params.measurementIndex] ?? null,
    measurementDefinitionId: measurementDefinition?.id ?? 0,
    measurementIndex: params.measurementIndex,
    hasQuestions: (measurementDefinition?.questions.length ?? 0) > 0,
    hasRecording:
      params.confirmedRecordingUris[params.measurementIndex] != null ||
      params.confirmedRecordingBlobs[params.measurementIndex] != null,
    requiresRecording: measurementRequiresRecording(measurementDefinition),
    questionCount: measurementDefinition?.questions.length ?? 0,
    answeredQuestionCount: countAnsweredVoiceAssessmentQuestions(answers),
  }
}
